import {
  Component,
  OnDestroy,
  OnInit,
  viewChild,
  inject,
  ChangeDetectionStrategy,
} from '@angular/core';
import {
  UntypedFormBuilder,
  UntypedFormGroup,
  Validators,
  FormsModule,
  ReactiveFormsModule,
} from '@angular/forms';
import { TranslateService, TranslatePipe } from '@ngx-translate/core';
import {
  ConfirmationService,
  FilterMetadata,
  SelectItem,
  PrimeTemplate,
} from '@openng/optimus-ui/api';
import { forkJoin, Subscription } from 'rxjs';
import { take } from 'rxjs/operators';
import {
  LazyLoadFilters,
  PageRequest,
  SearchContainerComponent,
} from 'src/app/core/containers/search-container/search-container.component';
import { TabService } from 'src/app/core/services/tab.service';
import { MOCEventCatInput } from 'src/app/model/admin/moc-event-cat-input';
import { DirtyStatus } from 'src/app/model/common/dirty-status';
import { MOCEventCategory } from 'src/app/model/crm/moc-event-category';
import { AdminBuilderService } from 'src/app/services/builder/admin-builder.service';
import { AdminApiService } from 'src/app/services/api/admin-api.service';
import { LogAndMessageService } from 'src/app/services/log-and-message.service';
import { MocEventApiService } from 'src/app/services/api/moc-event-api.service';
import { MOC_EVENT_CATEGORY_TABLE_DEFINITION } from '../../definitions/data-table-definitions';
import { isValidMocEventCategoryInput } from 'src/app/crm/functions/moc-event-functions';
import { AuthApiService } from 'src/app/services/api/auth-api.service';
import { ShiftTurnoverApiService } from 'src/app/services/api/shift-turnover-api.service';
import { BusinessUnit } from 'src/app/model/admin/business-unit';
import { BusinessUnitFilterComponent } from 'src/app/core/components/business-unit-filter/business-unit-filter.component';
import { isNullOrUndefined } from 'src/app/utils/utils';
import { CLAIMS } from 'src/app/constants/auth-constants';
import { MOCEventCategoryTableService } from 'src/app/services/table/moc-event-category-table.service';
import { ShiftGroup } from 'src/app/model/shift/shift-group';
import { GridLazyLoadEvent } from 'src/app/core/components/base-grid.component';
import { CheckboxChangeEvent, Checkbox } from '@openng/optimus-ui/checkbox';
import { BlockUI } from '@openng/optimus-ui/blockui';
import { ProgressSpinnerComponent } from '../../../core/components/progress-spinner.component';
import { BusinessUnitFilterComponent as BusinessUnitFilterComponent_1 } from '../../../core/components/business-unit-filter/business-unit-filter.component';
import { ButtonDirective } from '@openng/optimus-ui/button';
import { GeneralGridComponent } from '../../../core/components/general-grid.component';
import { Dialog } from '@openng/optimus-ui/dialog';
import { InputText } from '@openng/optimus-ui/inputtext';
import { PickList } from '@openng/optimus-ui/picklist';

@Component({
  selector: 'app-moc-event-category-container',
  templateUrl: './moc-event-category-container.component.html',
  styleUrls: ['./moc-event-category-container.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [
    BlockUI,
    ProgressSpinnerComponent,
    BusinessUnitFilterComponent_1,
    ButtonDirective,
    GeneralGridComponent,
    Dialog,
    FormsModule,
    ReactiveFormsModule,
    InputText,
    Checkbox,
    PickList,
    PrimeTemplate,
    TranslatePipe,
  ],
})
export class MocEventCategoryContainerComponent
  extends SearchContainerComponent<MOCEventCategory>
  implements OnInit, OnDestroy
{
  protected _mocApi = inject(MocEventApiService);
  protected _adminBuilder = inject(AdminBuilderService);
  protected _translateService = inject(TranslateService);
  protected _logAndMessage = inject(LogAndMessageService);
  protected _tabService = inject(TabService);
  protected _mocEventCategoryTableService = inject(
    MOCEventCategoryTableService
  );
  protected _fb = inject(UntypedFormBuilder);
  protected _adminApi = inject(AdminApiService);
  protected _confirmationService = inject(ConfirmationService);
  protected _authApi = inject(AuthApiService);
  protected _shiftApi = inject(ShiftTurnoverApiService);

  readonly businessUnitFilter =
    viewChild<BusinessUnitFilterComponent>('buFilter');

  canCreate = false;
  canEdit = false;
  canDelete = false;
  saving = false;
  form!: UntypedFormGroup;
  displayDialog = false;
  dialogHeader!: string;
  groups: SelectItem[] = [];

  categories: MOCEventCategory[] = [];
  selectedCategory?: MOCEventCategory;
  showSuccessMessage = true;
  sub!: Subscription;

  activeTabChangedSub!: Subscription;
  hasLoaded = false;

  availableBusinessUnits: BusinessUnit[] = [];
  sourcePicklist: BusinessUnit[] = [];
  targetPicklist: BusinessUnit[] = [];

  constructor() {
    super();
  }

  ngOnInit() {
    this._mocEventCategoryTableService.setLastLazyLoad(null);
    this.setTableService(this._mocEventCategoryTableService);
    this.setupActiveTabChangedSubscription();
    this.form = this._fb.group({
      id: [null],
      name: [{ value: null, disabled: false }, Validators.required],
      enabled: [{ value: false, disabled: false }],
      version: [null],
      businessUnits: [{ value: null, disabled: false }],
    });

    this.columns = MOC_EVENT_CATEGORY_TABLE_DEFINITION;

    const screenName$ = this._translateService.get(
      'CRM.SCREEN.MOC_EVENT_CATEGORIES'
    );
    const dialogHeader$ = this._translateService.get(
      'CRM.LABEL.MOC_EVENT_CATEGORY_DETAILS'
    );
    forkJoin([screenName$, dialogHeader$]).subscribe((label) => {
      this._tabService.updateActiveTabLabel(label[0]);
      this.screenName = label[0];
      this.dialogHeader = label[1];
    });

    this._adminApi.getAvailableBusinessUnits().subscribe((businessUnits) => {
      this.availableBusinessUnits = businessUnits;
    });

    this.canCreate = this._authApi.doesUserHaveAllClaimsFromList([
      CLAIMS.CONTROL_ROOM.MOC_EVENTS.CATEGORIES.CREATE_MOC_EVENT_CATEGORIES,
    ]);
    this.canEdit = this._authApi.doesUserHaveAllClaimsFromList([
      CLAIMS.CONTROL_ROOM.MOC_EVENTS.CATEGORIES.EDIT_MOC_EVENT_CATEGORIES,
    ]);
    this.canDelete = this._authApi.doesUserHaveAllClaimsFromList([
      CLAIMS.CONTROL_ROOM.MOC_EVENTS.CATEGORIES.DELETE_MOC_EVENT_CATEGORIES,
    ]);
    this.form.disable();

    this._shiftApi.getShiftGroups().subscribe((groups) => {
      this.groups = [
        { label: 'Choose', value: null },
        ...groups.map((sg) => ({
          label: sg.name,
          value: sg.id,
        })),
      ];
      this.applyPreviousData();
      this.applyPreviousFilters();
    });
  }

  ngOnDestroy(): void {
    if (this.activeTabChangedSub) {
      this.activeTabChangedSub.unsubscribe();
    }
  }

  override handleLazyLoad(
    req: PageRequest,
    $event: GridLazyLoadEvent,
    filters: LazyLoadFilters
  ) {
    this._mocEventCategoryTableService.setLastLazyLoad($event.lazy);
    const query: any = {
      name: null,
      enabled: null,
      businessUnits: null,
      businessUnitString: null,
    };

    let selectedBusinessUnits = [];
    const businessUnitFilter = this.businessUnitFilter();
    if (businessUnitFilter?.selectedBusinessUnits) {
      // Handle further refreshes & changes to business unit filter component
      selectedBusinessUnits = businessUnitFilter.selectedBusinessUnits;
    } else {
      // Handle initial page load (before the business unit filter subcomponent exists)
      selectedBusinessUnits = this._authApi
        .getUserBusinessUnits()
        .map((bu) => bu.id);
    }
    query.businessUnits = selectedBusinessUnits;
    if (filters.name) {
      query.name = (filters.name as FilterMetadata).value;
    }
    if (
      filters.enabled &&
      !isNullOrUndefined((filters.enabled as FilterMetadata).value)
    ) {
      if ((filters.enabled as FilterMetadata).value) {
        query.enabled = 'true';
      } else {
        query.enabled = 'false';
      }
    }
    if (filters.businessUnitString) {
      query.businessUnitString = (
        filters.businessUnitString as FilterMetadata
      ).value;
    }

    this.queryNetwork(req, $event, query);
  }

  handleBusinessUnitChange() {
    this.lazyLoad({
      lazy: this._mocEventCategoryTableService.getLastLazyLoad()!,
    });
  }

  queryNetwork(
    req: PageRequest,
    $event: GridLazyLoadEvent,
    query: { [key: string]: any }
  ): void {
    req.page += 1;
    const sort = this.buildNetworkSortQuery($event, 'name');
    this._mocApi
      .queryForMOCEventCategories(req.pageSize, req.page, query, sort)
      .subscribe({
        next: (connection) => {
          this.categories = connection.items;
          this.totalRecords = connection.totalRecords;
          this.elements = [...this.categories];
          this.hasLoaded = true;
        },
        error: (error) => {
          this.loading = false;
          this._logAndMessage.errorLogOnly(error);
          this._logAndMessage.translateToErrorAlert({
            bodyKey: 'CRM.MESSAGES.ERROR.QUERY_MOC_EVENT_CATEGORIES',
            headerKey: 'COMMON.MESSAGES.HEADERS.ERROR',
          });
        },
        complete: () => {
          this.loading = false;
          if (this.showSuccessMessage) {
            this.showSuccessMessage = false;
            this._logAndMessage.translateToSuccessMessage({
              bodyKey: 'CRM.MESSAGES.SUCCESS.QUERY_MOC_EVENT_CATEGORIES',
              headerKey: 'COMMON.MESSAGES.HEADERS.SUCCESS',
            });
          }
        },
      });
  }

  // TODO: better define this object, its probably something like an MOCEventCategory but that type doesnt work perfectly
  changeViewEvent($event: { data: any }): void {
    this.selectedCategory = $event.data;
    this.form.patchValue({
      id: $event.data.id,
      name: $event.data.name,
      // shiftGroup: ($event.data.ShiftGroup ? $event.data.ShiftGroup.id : null), // DEFault to gas control
      enabled: $event.data.enabled,
      version: $event.data.version,
      businessUnits: $event.data.businessUnits,
    });

    const businessUnits = this.form.controls['businessUnits'].value;
    let currentBusinessUnits: string[] = [];
    businessUnits.forEach((x: BusinessUnit) => {
      currentBusinessUnits.push(x.name);
    });
    this.sourcePicklist = [];
    this.targetPicklist = [];
    this.availableBusinessUnits.forEach((x) => {
      if (currentBusinessUnits.includes(x.name)) {
        this.targetPicklist.push(x);
      } else {
        this.sourcePicklist.push(x);
      }
    });

    if (this.canEdit) {
      this.form.enable();
    } else {
      this.form.disable();
    }
    this.displayDialog = true;
  }

  clearForm(): void {
    this.form.reset();
  }

  dialogClosed(): void {
    this.clearForm();
    this.selectedCategory = undefined;
  }

  newMocEventCategory(): void {
    this.form.reset();
    this.form.controls['enabled'].patchValue(false, { emitEvent: true });
    this.sourcePicklist = [];
    this.targetPicklist = [];
    this.availableBusinessUnits.forEach((x) => {
      this.sourcePicklist.push(x);
    });

    if (this.canCreate) {
      this.form.enable();
    } else {
      this.form.disable();
    }
    this.displayDialog = true;
  }

  refresh(): void {
    this.lazyLoad({
      lazy: this._mocEventCategoryTableService.getLastLazyLoad()!,
    });
  }

  nameChanged(): void {
    if (this.selectedCategory) {
      this.selectedCategory.name = this.form.controls['name'].value;
    }
  }

  toggleRecord($event: CheckboxChangeEvent): void {
    if (this.selectedCategory) {
      this.selectedCategory.enabled = $event.checked;
    }
  }

  saveMocEventCategory(): void {
    if (!(this.canCreate || this.canEdit)) {
      return;
    }

    if (!isNullOrUndefined(this.form) && this.form.valid) {
      let category = null;
      if (!isNullOrUndefined(this.selectedCategory)) {
        category = this.selectedCategory;
      } else {
        category = Object.assign({}, this.form.getRawValue());
      }
      if (!category.id) {
        this.createMocEventCategory(category);
      } else {
        this.updateMocEventCategory(category, true);
      }
    }
  }

  createMocEventCategory(category: MOCEventCategory) {
    if (!this.canCreate) {
      this._logAndMessage.error(
        'Cannot create moc event category!',
        'Logged in user does not have appropriate claim.'
      );
      return;
    }

    const shiftGroup = this.groups.find(
      (sg) => sg.label === 'Gas Control Room'
    );
    const categoryInput: MOCEventCatInput | null =
      this._adminBuilder.buildMOCEventCatInput(
        category,
        true,
        this.targetPicklist
      );
    if (shiftGroup && categoryInput) {
      categoryInput['shiftGroupId'] = shiftGroup.value;
    }
    const isValid = isValidMocEventCategoryInput(
      categoryInput,
      this.categories
    );
    if (!isNullOrUndefined(isValid)) {
      this._logAndMessage.translateToErrorAlert({
        headerKey: 'COMMON.MESSAGES.HEADERS.ERROR',
        bodyKey: isValid,
      });
      return;
    }
    this.saving = true;
    this._mocApi.createMOCEventCategory(categoryInput).subscribe({
      next: (result) => {
        this.clearForm();
        this.displayDialog = false;
      },
      error: (error) => {
        this.saving = false;
        this._logAndMessage.errorLogOnly(error);
        this._logAndMessage.translateToErrorAlert({
          bodyKey: 'CRM.MESSAGES.ERROR.CREATE_MOC_EVENT_CATEGORY',
          bodyParams: { name: category.name },
          headerKey: 'COMMON.MESSAGES.HEADERS.ERROR',
        });
      },
      complete: () => {
        this.saving = false;
        this._logAndMessage.translateToSuccessMessage({
          bodyKey: 'CRM.MESSAGES.SUCCESS.CREATE_MOC_EVENT_CATEGORY',
          bodyParams: { name: category.name },
          headerKey: 'COMMON.MESSAGES.HEADERS.SUCCESS',
        });
        this.refresh();
      },
    });
  }

  updateMocEventCategory(changes: MOCEventCategory, closeDialog: boolean) {
    if (!this.canEdit) {
      this._logAndMessage.error(
        'Cannot edit moc event category!',
        'Logged in user does not have appropriate claim.'
      );
      return;
    }

    const shiftGroup = this.groups.find(
      (sg) => sg.label === 'Gas Control Room'
    );
    const categoryChanges: MOCEventCatInput | null =
      this._adminBuilder.buildMOCEventCatInput(
        changes,
        false,
        this.targetPicklist
      );
    if (shiftGroup && categoryChanges) {
      categoryChanges['shiftGroupId'] = shiftGroup.value;
    }
    const isValid = isValidMocEventCategoryInput(
      categoryChanges,
      this.categories
    );
    if (!isNullOrUndefined(isValid)) {
      this._logAndMessage.translateToErrorAlert({
        headerKey: 'COMMON.MESSAGES.HEADERS.ERROR',
        bodyKey: isValid,
      });
      return;
    }
    this.saving = true;
    this._mocApi.updateMocEventCategory(changes.id, categoryChanges).subscribe({
      next: (result) => {
        if (!closeDialog && !isNullOrUndefined(result)) {
          this.changeViewEvent({ data: result });
        } else {
          this.clearForm();
          this.displayDialog = false;
        }
      },
      error: (error) => {
        this.saving = false;
        this._logAndMessage.errorLogOnly(error);
        this._logAndMessage.translateToErrorAlert({
          bodyKey: 'CRM.MESSAGES.ERROR.UPDATE_MOC_EVENT_CATEGORY',
          bodyParams: { name: changes.name },
          headerKey: 'COMMON.MESSAGES.HEADERS.ERROR',
        });
      },
      complete: () => {
        this.saving = false;
        this._logAndMessage.translateToSuccessMessage({
          bodyKey: 'CRM.MESSAGES.SUCCESS.UPDATE_MOC_EVENT_CATEGORY',
          bodyParams: { name: changes.name },
          headerKey: 'COMMON.MESSAGES.HEADERS.SUCCESS',
        });
        this.refresh();
      },
    });
  }

  deleteMocEventCategory(): void {
    if (!this.canDelete) {
      this._logAndMessage.error(
        'Cannot delete moc event category!',
        'Logged in user does not have appropriate claim.'
      );
      return;
    }

    if (!isNullOrUndefined(this.form)) {
      const category = Object.assign({}, this.form.getRawValue());
      let confHeader = null,
        ok = null,
        cancel = null,
        message = null;
      const confHeader$ = this._translateService.get(
        'CRM.MESSAGES.HEADERS.DELETE_MOC_EVENT_CATEGORY'
      );
      const ok$ = this._translateService.get('COMMON.LABEL.BUTTONS.YES');
      const cancel$ = this._translateService.get('COMMON.LABEL.BUTTONS.NO');
      const message$ = this._translateService.get(
        'CRM.MESSAGES.CONFIRMATION.DELETE_MOC_EVENT_CATEGORY'
      );

      forkJoin([confHeader$, ok$, cancel$, message$]).subscribe((messages) => {
        confHeader = messages[0];
        ok = messages[1];
        cancel = messages[2];
        message = messages[3];
      });

      this._confirmationService.confirm({
        header: confHeader!,
        icon: 'fa fa-question-circle',
        acceptVisible: true,
        rejectVisible: true,
        acceptLabel: ok!,
        rejectLabel: cancel!,
        message: message!,
        accept: () => {
          this.saving = true;
          if (!isNullOrUndefined(category.id)) {
            // Existing ShiftMitigation so delete it
            this._mocApi
              .allowMOCEventCategoryDelete(category.id)
              .subscribe((canDelete) => {
                if (canDelete) {
                  this.saving = false;
                  this._logAndMessage.translateToErrorAlert({
                    bodyKey:
                      'CRM.MESSAGES.ERROR.ALLOW_DELETE_MOC_EVENT_CATEGORY',
                    headerKey: 'COMMON.MESSAGES.HEADERS.ERROR',
                  });
                } else {
                  this._mocApi.deleteMOCEventCategory(category.id).subscribe({
                    next: (result) => {
                      this.saving = false;
                      this.clearForm();
                      this.displayDialog = false;
                      this.refresh();
                    },
                    error: (error) => {
                      this._logAndMessage.errorLogOnly(error);
                      this._logAndMessage.translateToErrorAlert({
                        bodyKey: 'CRM.MESSAGES.ERROR.DELETE_MOC_EVENT_CATEGORY',
                        bodyParams: { name: category.name },
                        headerKey: 'COMMON.MESSAGES.HEADERS.ERROR',
                      });
                    },
                    complete: () => {
                      this._logAndMessage.translateToSuccessMessage({
                        bodyKey:
                          'CRM.MESSAGES.SUCCESS.DELETE_MOC_EVENT_CATEGORY',
                        bodyParams: { name: category.name },
                        headerKey: 'COMMON.MESSAGES.HEADERS.SUCCESS',
                      });
                    },
                  });
                }
              });
          } else {
            // new ShiftMitigation so just close dialog
            this.displayDialog = false;
          }
        },
        reject: () => {},
      });
    }
  }

  setupActiveTabChangedSubscription(): void {
    this.activeTabChangedSub = this._tabService.activeTabChanged.subscribe(
      (tab) => {
        if (!isNullOrUndefined(tab) && tab.header === this.screenName) {
          this.refresh();
        }
      }
    );
  }
}
